import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Snackbar, styled } from "@mui/material";
import Header from "@shared/header";
import BasicDetails, {
  BasicDetailsFormDefaultValues,
  IBasicDetailsForm,
} from "./basicDetails";
import BankDetails, {
  BankDetailsFormDefaultValues,
  IBankDetailsForm,
} from "./bankDetails";
import ConfirmDetails from "./confirmDetails";
import Wizard from "./wizardSteps";
import { getUrl } from "utils/core";
import { APIResource, KYCConstants } from "constants/application";
import { useDispatch, useSelector } from "react-redux";
import {
  IGenericInformation,
  setGenericInformation,
  setKycDone,
} from "store/slices/genericInformation";
import { IBankBranchDetails, setBankDetails, clearBankDetails } from "store/slices/ifscSlice";
import { setPanResponse, setPanStatus } from "store/slices/validatePAN";
import { setBankResponse, setBankStatus } from "store/slices/validateBank";
import axiosInstance from "utils/axiosInstance";
import {
  resolveKycDocumentDisplayUrl,
} from "utils/fetchKycDocumentPresignedUrl";
import Loader from "@shared/loader";
import { PAN_STATUS_VARIANTS } from "constants/panStatusVariants";
import { BANK_STATUS_VARIANTS } from "constants/bankStatusVariants";
import {
  KYC_STATUS_VARIANTS,
} from "constants/kycStatusVariants";
import { ROUTES_VARIANT } from "constants/routesVariant";
import { StepBar } from "./ocrSteps/pages/StepBar";
import { UploadDetails } from "./ocrSteps/pages/UploadDetails";
import { ButtonPanel } from "./ocrSteps/pages/ButtonPanel";
import { BankDetailsOCR, BankDetailsOCRPersistedState, chequeNameMismatchMessage, bankDetailsVerifiedMessage } from './ocrSteps/pages/BankDetailsOCR';
import { BasicDetailsOCR } from './ocrSteps/pages/BasicDetailsOCR';
import { SubmitDetails } from "./ocrSteps/pages/SubmitDetails";
import { KYCCompleted } from './ocrSteps/pages/KYCCompleted';
import KycUnderReview from "components/pages/finish/KycUnderReview";
import {
  isPostSubmitUnderReviewStatus,
  resolveRetailerKycEntry,
} from "utils/kycStatusRouting";

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  height: inherit;
`;

export interface IAllData extends IBasicDetailsForm, IBankDetailsForm {}

interface IBasicDetailsOCRContext {
  uploadedPanImage?: string | null;
  panStatus?: string;
  validationMessage?: string;
  formData: IAllData;
  panResponse?: {
    fullName?: string;
    [key: string]: unknown;
  } | null;
}

interface ISubmitReviewData {
  panResponse: Record<string, unknown> | null;
  bankDetails: Record<string, unknown> | null;
}

// Process COmponent which contains 3 step of KYC verification process.
function KYCsteps() {
  const { t } = useTranslation();
  const [step, setStep] = useState(-1);
  const [searchParams, setSearchParams] = useSearchParams();
  /** Hold the whole screen until customer GET resolves so the wizard never flashes. */
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [statusResolved, setStatusResolved] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const info = useSelector((state: any) => state?.info?.allData);
  const panValidation = useSelector((state: any) => state?.pan);
  const bankValidation = useSelector((state: any) => state?.bank);
  const [formData, setFormData] = useState<IAllData>({
    ...BasicDetailsFormDefaultValues,
    ...BankDetailsFormDefaultValues,
  });


  const [currentScreen, setCurrentScreen] = useState<'upload' | 'basicDetails' | 'bankDetails' | 'submitDetails' | 'kycCompleted'>('upload');
  /** GBS / unknown status: show read-only under-review screen; wizard must not mount. */
  const [showUnderReview, setShowUnderReview] = useState(false);
  /** VERIFIED resume: the account the bank screen was pre-filled with, to gate Submit on an actual change. */
  const [verifiedBankSnapshot, setVerifiedBankSnapshot] = useState<{ bankAccountNumber?: string; ifscCode?: string } | null>(null);
  /** Completed users can view PAN, then continue to Bank without restarting verification or adoption. */
  const [viewingPanReadOnly, setViewingPanReadOnly] = useState(false);
  /** Late joiner landed on SubmitDetails to confirm PAN-group adoption (not yet bank). */
  const [offerPanAdoption, setOfferPanAdoption] = useState(false);
  const [uploadedPanImage, setUploadedPanImage] = useState<string | null>(null);
  const [panValidationMessage, setPanValidationMessage] = useState<string>('');
  const [panUploadFailureCount, setPanUploadFailureCount] = useState(0);
  const [basicDetailsOCRContext, setBasicDetailsOCRContext] =
    useState<IBasicDetailsOCRContext | null>(null);
  const [bankDetailsOCRState, setBankDetailsOCRState] = useState<BankDetailsOCRPersistedState>({
    uploadedImageSrc: null,
    selectedAccountType: '',
    verifiedAccountHolderName: '',
    isBankVerified: false,
    nameMatched: undefined,
    validationMessage: '',
    bankUploadFailureCount: 0,
    isManualMode: false,
    manualBankAccountNumber: '',
    manualReEnterBankAccountNumber: '',
    manualIfscCode: '',
    manualIfscBankName: '',
    reasonForMismatch: '',
    customReasonForMismatch: '',
    errorMessage: '',
  });
  const [submitReviewData, setSubmitReviewData] = useState<ISubmitReviewData>({
    panResponse: null,
    bankDetails: null,
  });
  const [manualEntryState, setManualEntryState] = useState({
    panNumber: '',
    verifiedName: '',
    verifiedStatus: '',
    establishmentType: '',
    isManualMode: false,
  });
  const [prefillBankImageUrl, setPrefillBankImageUrl] = useState<{ imageUrl: string; objectKey: string; customerId: string; accountType: string } | null>(null);
  const lastBankNavigationPanRef = useRef<string>('');

  const appName = localStorage.getItem('appname') || 'SEMINIS';

  const clearChequeNameMismatchReason = useCallback(() => {
    setBankDetailsOCRState((prev) => ({
      ...prev,
      reasonForMismatch: '',
      customReasonForMismatch: '',
    }));
    setFormData((prev) => ({
      ...prev,
      nameMismatchReason: '',
    }));
    setSubmitReviewData((prev) => ({
      ...prev,
      bankDetails: prev.bankDetails
        ? {
            ...prev.bankDetails,
            nameMismatchReason: '',
          }
        : prev.bankDetails,
    }));
  }, []);

  const getEstablishmentTypeFromPanResponse = (
    response?: Record<string, unknown> | null
  ): string => {
    return String(
      (response?.establishmentType as string | undefined) ||
      ''
    ).trim();
  };

  const handleVerificationComplete = ({
    imageSrc,
    panStatus,
    panResponse,
    validationMessage,
  }: {
    imageSrc: string;
    panStatus: (typeof PAN_STATUS_VARIANTS)[keyof typeof PAN_STATUS_VARIANTS];
    panResponse: Record<string, unknown>;
    validationMessage?: string;
  }) => {
    const isPanVerified = panStatus === PAN_STATUS_VARIANTS.PAN_VERIFIED;
    const latestEstablishmentType = getEstablishmentTypeFromPanResponse(panResponse);
    setUploadedPanImage(imageSrc);
    setPanValidationMessage(validationMessage || '');
    setPanUploadFailureCount((prev) => (isPanVerified ? 0 : prev + 1));
    setManualEntryState({
      panNumber: '',
      verifiedName: '',
      verifiedStatus: '',
      establishmentType: '',
      isManualMode: false,
    });
    setFormData((prev) => ({
      ...prev,
      mobileNumber: info?.mobileNumber || prev.mobileNumber,
      establishmentType: latestEstablishmentType,
      panNumber: String((panResponse?.panNumber as string | undefined) || prev.panNumber || ''),
      tdsStatus: false,
      registeredBusinessName: prev.registeredBusinessName || '',
    }));
    dispatch(
      setGenericInformation({
        establishmentType: latestEstablishmentType,
      })
    );
    dispatch(setPanStatus({ value: panStatus }));
    dispatch(setPanResponse({ value: panResponse }));
    // setCurrentScreen(isPanVerified ? 'bankDetails' : 'upload');
    setCurrentScreen('upload');
  };

  const handleManualPanValidationUpdate = ({
    panStatus,
    panResponse,
    validationMessage,
  }: {
    panStatus: (typeof PAN_STATUS_VARIANTS)[keyof typeof PAN_STATUS_VARIANTS];
    panResponse: Record<string, unknown>;
    validationMessage?: string;
  }) => {
    const isPanVerified = panStatus === PAN_STATUS_VARIANTS.PAN_VERIFIED;
    const latestEstablishmentType = getEstablishmentTypeFromPanResponse(panResponse);
    setPanValidationMessage(validationMessage || '');
    setPanUploadFailureCount((prev) => (isPanVerified ? 0 : prev));
    setFormData((prev) => ({
      ...prev,
      establishmentType: latestEstablishmentType || prev.establishmentType || '',
      panNumber: String((panResponse?.panNumber as string | undefined) || prev.panNumber || ''),
      registeredBusinessName: prev.registeredBusinessName || '',
    }));
    if (latestEstablishmentType) {
      dispatch(
        setGenericInformation({
          establishmentType: latestEstablishmentType,
        })
      );
    }
    dispatch(setPanStatus({ value: panStatus }));
    dispatch(setPanResponse({ value: panResponse }));
  };

  const fetchAssociatedBankImageUrl = async (customerId: string): Promise<{ imageUrl: string; objectKey: string; customerId: string; accountType: string } | null> => {
    try {
      const customerSource = localStorage.getItem('appname') || 'BRP';
      const response = await axiosInstance.get(
        getUrl(APIResource.validateBank, [
          {
            variableName: 'customerId',
            variableValue: customerId,
          },
        ], [{ paramName: 'customerSource', paramValue: customerSource }])
      );
      if (response.status === 200 && response.data && response.data.accountProofDocumentUrl) {
        const objectKey = response.data.accountProofDocumentUrl as string;
        const imageUrl = await resolveKycDocumentDisplayUrl({
          customerId,
          customerSource,
          documentType: 'BANK_ACCOUNT_PROOF',
          objectKey,
        });
        if (!imageUrl) {
          return null;
        }
        return {
          imageUrl,
          objectKey,
          customerId,
          accountType: response.data.accountType || '',
        };
      }
    } catch (err) {
      console.error('Failed to fetch associated bank details', err);
    }
    return null;
  };

  const handleNextFromBasicDetails = async (
    data: IBankDetailsForm | IBasicDetailsForm,
    basicDetailsOCRProps?: IBasicDetailsOCRContext
  ) => {
    const previousRegisteredBusinessName = String(formData?.registeredBusinessName || '').trim();
    const incomingRegisteredBusinessName = String(
      (data as IBasicDetailsForm)?.registeredBusinessName || ''
    ).trim();
    const isFirmNameChanged = previousRegisteredBusinessName !== incomingRegisteredBusinessName;

    if (isFirmNameChanged) {
      clearChequeNameMismatchReason();
    }

    if (basicDetailsOCRProps) {
      setBasicDetailsOCRContext(basicDetailsOCRProps);
      setUploadedPanImage(basicDetailsOCRProps.uploadedPanImage || null);
      setPanValidationMessage(basicDetailsOCRProps.validationMessage || '');
      if (basicDetailsOCRProps.panStatus) {
        dispatch(setPanStatus({ value: basicDetailsOCRProps.panStatus }));
      }
      if (basicDetailsOCRProps.panResponse) {
        dispatch(setPanResponse({ value: basicDetailsOCRProps.panResponse }));
      }
    }
    setFormData((prev) => ({
      ...prev,
      ...data,
      mobileNumber: info?.mobileNumber || prev.mobileNumber,
      establishmentType:
        (data as IBasicDetailsForm).establishmentType ||
        prev.establishmentType ||
        info?.establishmentType ||
        '',
      panNumber: String((panValidation?.panResponse?.panNumber as string | undefined) || prev.panNumber || ''),
      tdsStatus:
        panValidation?.panStatus === PAN_STATUS_VARIANTS.PAN_UNAVAILABLE_CHECKED,
      registeredBusinessName:
        (data as IBasicDetailsForm).registeredBusinessName || prev.registeredBusinessName || '',
    }));
    setPanValidationMessage('');

    // Use the most up-to-date PAN response (from basicDetailsOCRProps if available, otherwise Redux)
    const latestPanResponse = basicDetailsOCRProps?.panResponse || panValidation?.panResponse;

    // Detect whether the PAN number has changed since last bank navigation
    const currentPanNumber = String(latestPanResponse?.panNumber || '').trim().toUpperCase();
    const previousPanNumber = lastBankNavigationPanRef.current;
    const isPanChanged = currentPanNumber !== previousPanNumber && previousPanNumber !== '';

    const associatedList = ((latestPanResponse?.associatedCustomerDataList || []) as any[])
      .filter((item: any) => String(item.customerId) !== String(info?.customerId));

    // Late joiner: same-PAN VERIFIED siblings exist — offer optional adoption.
    // Backend no longer adopts during PAN verification; the partner must confirm.
    if (associatedList.length > 0) {
      const panResponse =
        (latestPanResponse as Record<string, unknown>) || null;
      setSubmitReviewData({
        panResponse,
        bankDetails: {
          establishmentType:
            (data as IBasicDetailsForm).establishmentType ||
            info?.establishmentType ||
            '',
          registeredBusinessName:
            (data as IBasicDetailsForm).registeredBusinessName || '',
        },
      });
      lastBankNavigationPanRef.current = currentPanNumber;
      setOfferPanAdoption(true);
      setStep(3);
      setSearchParams({ step: '3' }, { replace: true });
      setCurrentScreen('submitDetails');
      return;
    }

    // Prefill bank image from associated customer if available
    await proceedToBankDetailsAfterPan(associatedList, currentPanNumber, isPanChanged);
  };

  const proceedToBankDetailsAfterPan = async (
    associatedList: any[],
    currentPanNumber: string,
    isPanChanged: boolean
  ) => {
    setOfferPanAdoption(false);

    // Prefill bank image from associated customer if available
    if (associatedList.length > 0 && (isPanChanged || !bankDetailsOCRState.isBankVerified)) {
      setLoading(true);
      const result = await fetchAssociatedBankImageUrl(associatedList[0].customerId);
      if (result) {
        setPrefillBankImageUrl(result);
        setBankDetailsOCRState({
          uploadedImageSrc: null,
          selectedAccountType: '',
          verifiedAccountHolderName: '',
          isBankVerified: false,
          nameMatched: undefined,
          validationMessage: '',
          bankUploadFailureCount: 0,
          isManualMode: false,
          manualBankAccountNumber: '',
          manualReEnterBankAccountNumber: '',
          manualIfscCode: '',
          manualIfscBankName: '',
          reasonForMismatch: '',
          customReasonForMismatch: '',
          errorMessage: '',
        });
      } else {
        // Associated customer has no bank details — ensure BankDetailsOCR starts blank
        setPrefillBankImageUrl(null);
        setBankDetailsOCRState({
          uploadedImageSrc: null,
          selectedAccountType: '',
          verifiedAccountHolderName: '',
          isBankVerified: false,
          nameMatched: undefined,
          validationMessage: '',
          bankUploadFailureCount: 0,
          isManualMode: false,
          manualBankAccountNumber: '',
          manualReEnterBankAccountNumber: '',
          manualIfscCode: '',
          manualIfscBankName: '',
          reasonForMismatch: '',
          customReasonForMismatch: '',
          errorMessage: '',
        });
      }
      setLoading(false);
    } else if (isPanChanged) {
      // PAN changed and no associated customers — reset bank to blank state
      setPrefillBankImageUrl(null);
      setBankDetailsOCRState({
        uploadedImageSrc: null,
        selectedAccountType: '',
        verifiedAccountHolderName: '',
        isBankVerified: false,
        nameMatched: undefined,
        validationMessage: '',
        bankUploadFailureCount: 0,
        isManualMode: false,
        manualBankAccountNumber: '',
        manualReEnterBankAccountNumber: '',
        manualIfscCode: '',
        manualIfscBankName: '',
        reasonForMismatch: '',
        customReasonForMismatch: '',
        errorMessage: '',
      });
      // Clear Redux bank state so component doesn't show stale data
      dispatch(setBankResponse({ value: null }));
      dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.PENDING }));
      dispatch(clearBankDetails());
    }

    // Track the PAN used for this bank navigation
    lastBankNavigationPanRef.current = currentPanNumber;

    setStep(2);
    setSearchParams({ step: '2' }, { replace: true });
    setCurrentScreen('bankDetails');
  };

  const handleContinueWithoutPanAdoption = async () => {
    const latestPanResponse = basicDetailsOCRContext?.panResponse || panValidation?.panResponse;
    const currentPanNumber = String(latestPanResponse?.panNumber || '').trim().toUpperCase();
    const associatedList = ((latestPanResponse?.associatedCustomerDataList || []) as any[])
      .filter((item: any) => String(item.customerId) !== String(info?.customerId));
    await proceedToBankDetailsAfterPan(associatedList, currentPanNumber, true);
  };

  const handlePanAdoptionSuccess = (kycStatus: string) => {
    setOfferPanAdoption(false);
    const entry = resolveRetailerKycEntry(kycStatus);
    if (entry.type === 'under_review') {
      setShowUnderReview(true);
      return;
    }
    if (!isPostSubmitUnderReviewStatus(kycStatus)) {
      setCurrentScreen('kycCompleted');
      return;
    }
    void handleContinueWithoutPanAdoption();
  };

  const handleBackToBasicDetails = () => {
    setCurrentScreen('upload');
  };

  /** Reuse the existing PAN step with its saved image and verification result. */
  const handleViewPanDetails = () => {
    setViewingPanReadOnly(true);
    setStep(1);
    setSearchParams({ step: "1" }, { replace: true });
    setCurrentScreen('upload');
  };

  const handleBackFromPanReadOnlyView = () => {
    setViewingPanReadOnly(false);
    setStep(2);
    setSearchParams({ step: "2" }, { replace: true });
    setCurrentScreen('bankDetails');
  };

  const handleNextFromBankDetails = (data: IBankDetailsForm | IBasicDetailsForm) => {
    const mergedFormData = { ...formData, ...data } as IAllData;
    const panResponse =
      panValidation?.panResponse || basicDetailsOCRContext?.panResponse || info?.panDetails;
    const bankResponse = {
      ...(info?.bankDetails || {}),
      ...(bankValidation?.bankResponse || {}),
    };

    setFormData(mergedFormData);
    setSubmitReviewData({
      panResponse: (panResponse as Record<string, unknown>) || null,
      bankDetails: {
        ...(info?.bankDetails || {}),
        ...(bankValidation?.bankResponse || {}),
        bankName:
          (bankResponse?.bankName as string | undefined) ||
          (bankResponse?.bank as string | undefined) ||
          mergedFormData?.ifscCode?.bank ||
          '',
        accountType:
          mergedFormData?.accType ||
          (bankResponse?.accountType as string | undefined) ||
          '',
        bankAccountNumber:
          (bankResponse?.bankAccountNumber as string | undefined) ||
          mergedFormData?.bankAccountNumber ||
          '',
        ifscCode:
          (bankResponse?.ifscCode as string | undefined) ||
          mergedFormData?.ifscCode?.ifscCode ||
          '',
        accountHolderName:
          (bankResponse?.accountHolderName as string | undefined) ||
          (bankResponse?.userProvidedName as string | undefined) ||
          info?.ownerName ||
          '',
        nameMatched:
          (bankResponse?.nameMatched as boolean | undefined) ?? bankDetailsOCRState?.nameMatched,
        establishmentType: mergedFormData?.establishmentType || info?.establishmentType || '',
        registeredBusinessName:
          (mergedFormData as IBasicDetailsForm)?.registeredBusinessName || '',
        nameMismatchReason:
          String((mergedFormData as IBankDetailsForm)?.nameMismatchReason || ''),
        gstCertificateDocumentUrl:
          String((mergedFormData as IBankDetailsForm)?.gstCertificateDocumentUrl || ''),
        gstCertificatePublicUrl:
          String((mergedFormData as IBankDetailsForm)?.gstCertificatePublicUrl || ''),
        gstNumber:
          String((mergedFormData as IBankDetailsForm)?.gstNumber || ''),
        gstFirmName:
          String((mergedFormData as IBankDetailsForm)?.gstFirmName || ''),
        isGstVerified:
          Boolean((mergedFormData as IBankDetailsForm)?.isGstVerified),
      },
    });
    setCurrentScreen('submitDetails');
  };

  const handleDeletePanImage = () => {
    clearChequeNameMismatchReason();
    setUploadedPanImage(null);
    setPanValidationMessage('');
    setBasicDetailsOCRContext(null);
    setFormData((prev) => ({
      ...prev,
      establishmentType: '',
      panNumber: '',
      registeredBusinessName: '',
    }));
    dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PENDING }));
    dispatch(setPanResponse({ value: null }));
    dispatch(
      setGenericInformation({
        establishmentType: '',
      })
    );
    setManualEntryState({
      panNumber: '',
      verifiedName: '',
      verifiedStatus: '',
      establishmentType: '',
      isManualMode: false,
    });
    setCurrentScreen('upload');
  };

  const handleBackFromSubmitDetails = () => {
    // Adoption offer skipped Bank A/C — return to PAN step
    if (offerPanAdoption) {
      setOfferPanAdoption(false);
      setStep(1);
      setSearchParams({ step: '1' }, { replace: true });
      setCurrentScreen('upload');
      return;
    }
    setCurrentScreen('bankDetails');
  };

  const handleCompleteFromSubmitDetails = () => {
    setCurrentScreen('kycCompleted');
  };

  useEffect(() => {
    // To get the customer details from the API and navigate user to appropriate step based on the kycStatus key
    const getCustomerDetails = async () => {
      setError("");
      setLoading(true);
      try {
        const appName = localStorage.getItem('appname');
        const response = await axiosInstance.get(
          getUrl(APIResource.customerDetails, [
            {
              variableName: "customerId",
              variableValue: info?.customerId,
            },
          ],  [{paramName: "customerSource", paramValue:  appName?appName:'BRP'}])
        );
        if (response.status === 200 && response.data) {
          if (response.data?.error) {
            setError(response.data?.message);
            setStatusResolved(true);
          } else {
            const data = response.data as IGenericInformation;
            const customerSource = appName ? appName : "BRP";
            const customerId = info?.customerId as string;
            const resolvePanUrl = (objectKey?: string | null) =>
              resolveKycDocumentDisplayUrl({
                customerId,
                customerSource,
                documentType: "PAN_CARD_PROOF",
                objectKey,
              });
            const resolveBankUrl = (objectKey?: string | null) =>
              resolveKycDocumentDisplayUrl({
                customerId,
                customerSource,
                documentType: "BANK_ACCOUNT_PROOF",
                objectKey,
              });
            const resolveGstUrl = (objectKey?: string | null) =>
              resolveKycDocumentDisplayUrl({
                customerId,
                customerSource,
                documentType: "GST_CERTIFICATE_PROOF",
                objectKey,
              });
            dispatch(setGenericInformation(data));
            // Reset unless the VERIFIED branch below re-establishes it for this load.
            setVerifiedBankSnapshot(null);
            setViewingPanReadOnly(false);
            let routeToUnderReview = false;
            const emptyBankOcrState: BankDetailsOCRPersistedState = {
              uploadedImageSrc: null,
              selectedAccountType: "",
              verifiedAccountHolderName: "",
              isBankVerified: false,
              nameMatched: undefined,
              validationMessage: "",
              bankUploadFailureCount: 0,
              isManualMode: false,
              manualBankAccountNumber: "",
              manualReEnterBankAccountNumber: "",
              manualIfscCode: "",
              manualIfscBankName: "",
              reasonForMismatch: "",
              customReasonForMismatch: "",
              errorMessage: "",
            };
            const entry = resolveRetailerKycEntry(data.kycStatus);
            if (entry.type === "under_review") {
              if (entry.reason === "unknown") {
                console.warn(
                  "Unrecognized KYC status; showing under-review screen:",
                  data.kycStatus
                );
              }
              routeToUnderReview = true;
            } else if (entry.type === "wizard" && entry.mode === "fresh") {
              if (entry.clearPriorData) {
                // REJECTED Re-KYC: start at Upload PAN with no prior data; only a PAN write is accepted.
                dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PENDING }));
                dispatch(setPanResponse({ value: null }));
                dispatch(setBankResponse({ value: null }));
                dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.PENDING }));
                dispatch(clearBankDetails());
                setFormData({
                  ...BasicDetailsFormDefaultValues,
                  ...BankDetailsFormDefaultValues,
                  mobileNumber: data?.mobileNumber || "",
                });
                setUploadedPanImage(null);
                setBankDetailsOCRState(emptyBankOcrState);
                setSubmitReviewData({ panResponse: null, bankDetails: null });
              }
              setStep(1);
              setSearchParams({ step: "1" }, { replace: true });
              setCurrentScreen("upload");
            } else {
            switch (data.kycStatus) {
              case KYC_STATUS_VARIANTS.PAN_VERIFIED:
                // User has verified the PAN update form and navigate user to bank verification step
                setStep(2);
                setSearchParams(
                  {
                    step: "2",
                  },
                  { replace: true }
                );
                // set default form values
                setFormData((prev) => {
                  const newFormData: IAllData = {
                    ...prev,
                    mobileNumber: data?.mobileNumber,
                    panNumber: data?.panDetails?.panNumber || "",
                    establishmentType: data.establishmentType,
                    tdsStatus: false,
                  };
                  return { ...newFormData };
                });
                panStatusSetting(data);
                if (data?.panDetails?.uploadedDocumentKey) {
                  const panUrl = await resolvePanUrl(
                    data.panDetails.uploadedDocumentKey
                  );
                  if (panUrl) {
                    setUploadedPanImage(panUrl);
                  }
                }
                break;
              case KYC_STATUS_VARIANTS.BANK_VERIFIED:
                // User has verified the Bank, navigate to bank details page
                setStep(2);
                setSearchParams(
                  {
                    step: "2",
                  },
                  { replace: true }
                );
                // set form default values
                setFormData((prev) => {
                  const newFormData: IAllData = {
                    ...prev,
                    mobileNumber: data.mobileNumber,
                    panNumber: data?.panDetails?.panNumber || "",
                    establishmentType: data.establishmentType,
                    tdsStatus: data?.panDetails ? false : true,
                    bankAccountNumber: data?.bankDetails?.bankAccountNumber,
                    reEnterBankAccountNumber:
                      data?.bankDetails?.bankAccountNumber,
                    accType: data?.bankDetails?.accountType,
                    ifscCode: {
                      ifscCode: data?.bankDetails?.ifscCode,
                      bank: data?.bankDetails?.bankName,
                      address: data?.bankDetails?.bankAddress,
                    },
                    accountProofDocumentUrl: data?.bankDetails?.accountProofDocumentUrl,
                  };
                  return { ...newFormData };
                });
                // eslint-disable-next-line no-case-declarations
                const ifscDetails: IBankBranchDetails = {
                  ifscCode: data?.bankDetails?.ifscCode,
                  bank: data?.bankDetails?.bankName,
                  address: data?.bankDetails?.bankAddress,
                };
                if (data?.panDetails) {
                  panStatusSetting(data);
                } else {
                  dispatch(
                    setPanStatus({
                      value: PAN_STATUS_VARIANTS.PAN_UNAVAILABLE_CHECKED,
                    })
                  );
                  dispatch(setPanResponse({ value: null }));
                }
                dispatch(setBankDetails({ value: ifscDetails }));
                dispatch(setBankResponse({ value: data.bankDetails }));
                dispatch(
                  setBankStatus({ value: BANK_STATUS_VARIANTS.BANK_VERIFIED })
                );
                // Seed nameMatched from the server unconditionally — proof image is decoration.
                if (data?.bankDetails) {
                  const resumedNameMatched = (data as any)?.bankDetails
                    ?.nameMatched as boolean | undefined;
                  setBankDetailsOCRState((prev) => ({
                    ...prev,
                    isBankVerified: true,
                    nameMatched: resumedNameMatched,
                    validationMessage:
                      resumedNameMatched === false
                        ? chequeNameMismatchMessage
                        : bankDetailsVerifiedMessage,
                    verifiedAccountHolderName:
                      data?.bankDetails?.accountHolderName || '',
                    selectedAccountType:
                      data?.bankDetails?.accountType || prev.selectedAccountType,
                    hasDeletedGstCertificate: false,
                  }));
                }
                if (data?.panDetails?.uploadedDocumentKey) {
                  const panUrl = await resolvePanUrl(
                    data.panDetails.uploadedDocumentKey
                  );
                  if (panUrl) {
                    setUploadedPanImage(panUrl);
                  }
                }
                if (data?.bankDetails?.accountProofDocumentUrl) {
                  const bankUrl = await resolveBankUrl(
                    data.bankDetails.accountProofDocumentUrl
                  );
                  if (bankUrl) {
                    setBankDetailsOCRState((prev) => ({
                      ...prev,
                      uploadedImageSrc: bankUrl,
                    }));
                  }
                }
                setCurrentScreen('bankDetails');
                break;
              case KYC_STATUS_VARIANTS.PAN_NOT_SUBMITTED:
                setStep(2);
                setSearchParams(
                  {
                    step: "2",
                  },
                  { replace: true }
                );
                setFormData((prev) => {
                  const newFormData: IAllData = {
                    ...prev,
                    panNumber: "",
                    establishmentType: data.establishmentType,
                    tdsStatus: true,
                  };
                  return { ...newFormData };
                });
                dispatch(
                  setPanStatus({
                    value: PAN_STATUS_VARIANTS.PAN_UNAVAILABLE_CHECKED,
                  })
                );
                dispatch(setPanResponse({ value: null }));
                break;
              case KYC_STATUS_VARIANTS.VERIFIED:
              case KYC_STATUS_VARIANTS.VERIFIED_NOT_SUBMITTED: {
                // Completed users land on Bank Details (view + optional edit) without repeating PAN verification; PAN stays view-only.
                setStep(2);
                setSearchParams({ step: "2" }, { replace: true });
                setFormData((prev) => {
                  const newFormData: IAllData = {
                    ...prev,
                    mobileNumber: data.mobileNumber,
                    panNumber: data?.panDetails?.panNumber || "",
                    establishmentType: data.establishmentType,
                    tdsStatus: data?.panDetails ? false : true,
                    bankAccountNumber: data?.bankDetails?.bankAccountNumber,
                    reEnterBankAccountNumber:
                      data?.bankDetails?.bankAccountNumber,
                    accType: data?.bankDetails?.accountType,
                    ifscCode: {
                      ifscCode: data?.bankDetails?.ifscCode,
                      bank: data?.bankDetails?.bankName,
                      address: data?.bankDetails?.bankAddress,
                    },
                    accountProofDocumentUrl: data?.bankDetails?.accountProofDocumentUrl,
                  };
                  return { ...newFormData };
                });
                if (data?.panDetails) {
                  panStatusSetting(data);
                } else {
                  dispatch(
                    setPanStatus({
                      value: PAN_STATUS_VARIANTS.PAN_UNAVAILABLE_CHECKED,
                    })
                  );
                  dispatch(setPanResponse({ value: null }));
                }
                if (data?.bankDetails) {
                  const ifscDetailsVerified: IBankBranchDetails = {
                    ifscCode: data?.bankDetails?.ifscCode,
                    bank: data?.bankDetails?.bankName,
                    address: data?.bankDetails?.bankAddress,
                  };
                  dispatch(setBankDetails({ value: ifscDetailsVerified }));
                  dispatch(setBankResponse({ value: data.bankDetails }));
                  dispatch(
                    setBankStatus({ value: BANK_STATUS_VARIANTS.BANK_VERIFIED })
                  );
                  setVerifiedBankSnapshot({
                    bankAccountNumber: data.bankDetails.bankAccountNumber,
                    ifscCode: data.bankDetails.ifscCode,
                  });
                  const resumedNameMatched = (data as any)?.bankDetails
                    ?.nameMatched as boolean | undefined;
                  setBankDetailsOCRState((prev) => ({
                    ...prev,
                    isBankVerified: true,
                    nameMatched: resumedNameMatched,
                    validationMessage:
                      resumedNameMatched === false
                        ? chequeNameMismatchMessage
                        : bankDetailsVerifiedMessage,
                    verifiedAccountHolderName:
                      data?.bankDetails?.accountHolderName || '',
                    selectedAccountType:
                      data?.bankDetails?.accountType || prev.selectedAccountType,
                    hasDeletedGstCertificate: false,
                  }));
                }
                setCurrentScreen('bankDetails');
                break;
              }
              case KYC_STATUS_VARIANTS.GST_VERIFIED: {
                // User has uploaded GST certificate, navigate to bank details page
                setStep(2);
                setSearchParams({ step: "2" }, { replace: true });
                setFormData((prev) => ({
                  ...prev,
                  mobileNumber: data.mobileNumber,
                  panNumber: data?.panDetails?.panNumber || "",
                  establishmentType: data.establishmentType,
                  tdsStatus: data?.panDetails ? false : true,
                  bankAccountNumber: data?.bankDetails?.bankAccountNumber,
                  reEnterBankAccountNumber: data?.bankDetails?.bankAccountNumber,
                  accType: data?.bankDetails?.accountType,
                  ifscCode: {
                    ifscCode: data?.bankDetails?.ifscCode,
                    bank: data?.bankDetails?.bankName,
                    address: data?.bankDetails?.bankAddress,
                  },
                  accountProofDocumentUrl: data?.bankDetails?.accountProofDocumentUrl,
                }));
                if (data?.panDetails) {
                  panStatusSetting(data);
                } else {
                  dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PAN_UNAVAILABLE_CHECKED }));
                  dispatch(setPanResponse({ value: null }));
                }
                if (data?.bankDetails) {
                  const ifscDetailsGst: IBankBranchDetails = {
                    ifscCode: data?.bankDetails?.ifscCode,
                    bank: data?.bankDetails?.bankName,
                    address: data?.bankDetails?.bankAddress,
                  };
                  dispatch(setBankDetails({ value: ifscDetailsGst }));
                  dispatch(setBankResponse({ value: data.bankDetails }));
                  dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.BANK_VERIFIED }));
                }
                const resumedGstDocumentKey = String(
                  (data as any)?.gstDetails?.gstDocumentKey || ''
                );
                const resumedGstNumber = String(data?.gstDetails?.gstNumber || '');
                const resumedNameMatched = (data as any)?.bankDetails
                  ?.nameMatched as boolean | undefined;
                const [gstBankUrl, resumedGstUrl] = await Promise.all([
                  resolveBankUrl(data?.bankDetails?.accountProofDocumentUrl),
                  resolveGstUrl(resumedGstDocumentKey || null),
                ]);
                // Seed nameMatched + GST from the server unconditionally (proof URL is optional).
                setBankDetailsOCRState((prev) => ({
                  ...prev,
                  uploadedImageSrc: gstBankUrl || prev.uploadedImageSrc,
                  isBankVerified: true,
                  nameMatched: resumedNameMatched,
                  validationMessage:
                    resumedNameMatched === false
                      ? chequeNameMismatchMessage
                      : bankDetailsVerifiedMessage,
                  verifiedAccountHolderName: data?.bankDetails?.accountHolderName || '',
                  selectedAccountType: data?.bankDetails?.accountType || prev.selectedAccountType,
                  gstCertificateObjectKey: resumedGstDocumentKey,
                  gstCertificateFileName: resumedGstDocumentKey.split('/').pop() || '',
                  gstCertificatePublicUrl: resumedGstUrl || '',
                  gstNumber: resumedGstNumber,
                  gstFirmName: String(data?.gstDetails?.registeredBusinessName || ''),
                  isGstVerified: Boolean(resumedGstNumber),
                  gstValidationMessage: resumedGstNumber
                    ? 'Your firm name on GST matches your bank account name. Click Next to continue.'
                    : '',
                  hasDeletedGstCertificate: false,
                }));
                setSubmitReviewData({
                  panResponse: (data?.panDetails as unknown as Record<string, unknown>) || null,
                  bankDetails: {
                    ...(data?.bankDetails || {}),
                    bankName: data?.bankDetails?.bankName || '',
                    accountType: data?.bankDetails?.accountType || '',
                    bankAccountNumber: data?.bankDetails?.bankAccountNumber || '',
                    ifscCode: data?.bankDetails?.ifscCode || '',
                    accountHolderName: data?.bankDetails?.accountHolderName || '',
                    nameMatched: resumedNameMatched,
                    establishmentType: data?.establishmentType || '',
                    gstNumber: resumedGstNumber,
                    registeredBusinessName: String((data as any)?.gstDetails?.registeredBusinessName || ''),
                    gstFirmName: String((data as any)?.gstDetails?.registeredBusinessName || ''),
                    gstCertificateDocumentUrl: resumedGstDocumentKey || undefined,
                    gstCertificateObjectKey: resumedGstDocumentKey || undefined,
                    gstCertificatePublicUrl: resumedGstUrl || undefined,
                    isGstVerified: Boolean(resumedGstNumber),
                  },
                });
                setCurrentScreen('bankDetails');
                break;
              }
              default:
                // Known status with no dedicated resume branch — fail closed to under-review.
                console.warn(
                  "KYC status has no dedicated resume route; showing under-review screen:",
                  data.kycStatus
                );
                routeToUnderReview = true;
                break;
            }
            } // end resume wizard branch

            if (routeToUnderReview) {
              setShowUnderReview(true);
            } else if (
              data.kycStatus !== KYC_STATUS_VARIANTS.PENDING &&
              data.kycStatus !== KYC_STATUS_VARIANTS.KYC_INITIATED &&
              data.kycStatus !== KYC_STATUS_VARIANTS.REJECTED
            ) {
              // Prefill images for resumable wizard statuses only
              const [panUrl, bankUrl] = await Promise.all([
                resolvePanUrl(data?.panDetails?.uploadedDocumentKey),
                resolveBankUrl(data?.bankDetails?.accountProofDocumentUrl),
              ]);
              if (panUrl) {
                setUploadedPanImage(panUrl);
              }
              // Seed nameMatched from the server even when proof URL is missing/unresolvable.
              if (data?.bankDetails) {
                const resumedNameMatched = (data as any)?.bankDetails
                  ?.nameMatched as boolean | undefined;
                const resumedGstNumber = String(
                  data?.gstDetails?.gstNumber || ''
                );
                setBankDetailsOCRState((prev) => ({
                  ...prev,
                  ...(bankUrl ? { uploadedImageSrc: bankUrl } : {}),
                  isBankVerified: true,
                  nameMatched: resumedNameMatched,
                  validationMessage:
                    resumedNameMatched === false
                      ? chequeNameMismatchMessage
                      : bankDetailsVerifiedMessage,
                  verifiedAccountHolderName:
                    data?.bankDetails?.accountHolderName || '',
                  selectedAccountType:
                    data?.bankDetails?.accountType || prev.selectedAccountType,
                  gstNumber: resumedGstNumber || prev.gstNumber || '',
                  gstFirmName: String(
                    data?.gstDetails?.registeredBusinessName ||
                      prev.gstFirmName ||
                      ''
                  ),
                  isGstVerified:
                    Boolean(resumedGstNumber) &&
                    (data.kycStatus === KYC_STATUS_VARIANTS.VERIFIED ||
                      data.kycStatus === KYC_STATUS_VARIANTS.GST_VERIFIED),
                  gstValidationMessage:
                    Boolean(resumedGstNumber) &&
                    (data.kycStatus === KYC_STATUS_VARIANTS.VERIFIED ||
                      data.kycStatus === KYC_STATUS_VARIANTS.GST_VERIFIED)
                      ? 'Your firm name on GST matches your bank account name. Click Next to continue.'
                      : prev.gstValidationMessage,
                  hasDeletedGstCertificate: false,
                }));
              }
            }
            setStatusResolved(true);
          }
        } else {
          setStatusResolved(true);
        }
      } catch (err) {
        setError("Error Occurred while fetching customer details!");
        console.error("Error Occurred ", err);
        setStatusResolved(true);
      }
      setLoading(false);
    };

    // only if user comes through OTP login, allow KYC steps, else navigate user to home page
    if (info?.customerId) {
      getCustomerDetails();
    } else {
      setLoading(false);
      navigate("/", { replace: true });
    }
  }, []);

  // on change of url search param step value, set the step value using useeffect
  useEffect(() => {
    // only if user comes through OTP login, KYC step is relevant
    if (info?.customerId) {
      const _step = Number(location.search.split("=")[1]);
      if (_step) {
        setStep(_step);
      }
    }
  }, [searchParams]);

  function panStatusSetting(responseData: IGenericInformation) {
    if (!responseData.panDetails.isCompliant) {
      dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PAN_INACTIVE }));
    } else if (
      String(responseData?.panDetails?.panOperationalStatus).toUpperCase() ===
      "INOPERATIVE"
    ) {
      dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PAN_INOPERATIVE }));
    } else {
      dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PAN_VERIFIED }));
    }
    dispatch(setPanResponse({ value: responseData?.panDetails }));
  }
  // Step navigation
  const nextStep = useCallback(
    (partialData: IBankDetailsForm | IBasicDetailsForm) => {
      setFormData((prev) => ({ ...prev, ...partialData }));
      const _next = Math.min(step + 1, 3);
      setStep(_next);
      setSearchParams(
        {
          step: String(_next),
        },
        { replace: true }
      );
    },
    [step, setSearchParams]
  );

  return (
    <Container>
      {Boolean(info?.customerId) && (loading || !statusResolved) ? (
        <Loader />
      ) : showUnderReview ? (
        <KycUnderReview />
      ) : (
      // SEMINIS users also allow to follow new OCR flow, as per current discussion, Hence commenting out the condition for now.
      // appName === 'SEMINIS' ? (
      //   <>
      //     <Wizard step={step} />
      //     {step === 1 && <BasicDetails formData={formData} nextStep={nextStep} />}
      //     {step === 2 && <BankDetails formData={formData} nextStep={nextStep} />}
      //     {step === 3 && <ConfirmDetails />}
      //   </>
      // ) : (
        <>
          {currentScreen === 'upload' && (
            <>
              <Header />
              <StepBar currentStep={1} />
              <UploadDetails
                onVerificationComplete={handleVerificationComplete}
                basicDetailsProps={{
                  nextStep: viewingPanReadOnly ? handleBackFromPanReadOnlyView : handleNextFromBasicDetails,
                  onDelete: handleDeletePanImage,
                  uploadedPanImage,
                  panStatus: panValidation?.panStatus,
                  panResponse: panValidation?.panResponse,
                  validationMessage: panValidationMessage,
                  formData,
                }}
              />
              {!uploadedPanImage && <ButtonPanel />}
            </>
          )}
          {currentScreen === 'basicDetails' && (
            <>
              <Header />
              <StepBar currentStep={1} />
              <BasicDetailsOCR
                nextStep={viewingPanReadOnly ? handleBackFromPanReadOnlyView : handleNextFromBasicDetails}
                onDelete={handleDeletePanImage}
                uploadedPanImage={uploadedPanImage}
                panStatus={panValidation?.panStatus}
                panResponse={panValidation?.panResponse}
                validationMessage={panValidationMessage}
                formData={formData}
              />
            </>
          )}
          {currentScreen === 'bankDetails' && (
            <>
              <Header />
              <StepBar currentStep={2} />
              <BankDetailsOCR
                onBack={
                  info?.hasVerifiedActive ||
                  info?.kycStatus === KYC_STATUS_VARIANTS.VERIFIED ||
                  info?.kycStatus === KYC_STATUS_VARIANTS.VERIFIED_NOT_SUBMITTED
                    ? handleViewPanDetails
                    : handleBackToBasicDetails
                }
                nextStep={handleNextFromBankDetails}
                initialState={bankDetailsOCRState}
                onStateChange={setBankDetailsOCRState}
                establishmentType={formData?.establishmentType || info?.establishmentType}
                formData={formData}
                prefillImageUrl={prefillBankImageUrl}
                onPrefillImageConsumed={() => setPrefillBankImageUrl(null)}
                associatedCustomerDataList={
                  (panValidation?.panResponse?.associatedCustomerDataList || [])
                    .filter((item: any) => String(item.customerId) !== String(info?.customerId))
                    .map((item: any) => ({
                      customerId: item.customerId,
                      firmName: item.shopName || item.firmName || '',
                    }))
                }
                panNumber={String(panValidation?.panResponse?.panNumber || formData?.panNumber || '')}
                verifiedAccountSnapshot={verifiedBankSnapshot}
              />
            </>
          )}
          {currentScreen === 'submitDetails' && (
            <>
              <Header />
              <StepBar currentStep={3} />
              <SubmitDetails
                onBack={handleBackFromSubmitDetails}
                onComplete={handleCompleteFromSubmitDetails}
                onContinueWithoutAdopting={
                  offerPanAdoption ? handleContinueWithoutPanAdoption : undefined
                }
                onAdoptSuccess={handlePanAdoptionSuccess}
                customerId={String(info?.customerId || '')}
                reviewData={submitReviewData}
                associatedCustomerDataList={
                  (panValidation?.panResponse?.associatedCustomerDataList || [])
                    .filter((item: any) => String(item.customerId) !== String(info?.customerId))
                    .map((item: any) => ({
                      customerId: item.customerId,
                      firmName: item.shopName || item.firmName || '',
                    }))
                }
                panNumber={String(panValidation?.panResponse?.panNumber || formData?.panNumber || '')}
              />
            </>
          )}
          {currentScreen === 'kycCompleted' && (
            <>
              <Header />
              <StepBar currentStep={4} />
              <KYCCompleted
                hasGstCertificate={Boolean(bankDetailsOCRState?.gstCertificateObjectKey)}
                nameMatched={bankDetailsOCRState?.nameMatched}
              />
            </>
          )}
        </>
      // ) // SEMINIS users also allow to follow new OCR flow, as per current discussion, Hence commenting out the condition for now.
      )}
      <Snackbar
        open={Boolean(error)}
        onClose={() => setError("")}
        autoHideDuration={KYCConstants.autoHideDuration}
        message={error}
        sx={{ bottom: { xs: 90, sm: 0 } }}
      />
    </Container>
  );
}

export default KYCsteps;
